import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Somebody's signature, drawn with a finger on a reception tablet (FR-504).
 *
 * A canvas and pointer events — no library. The whole feature is "record the strokes and hand back
 * a PNG", and the two things that actually matter are not things a library would have got right
 * for us either.
 *
 * The first is that the canvas is sized in device pixels and scaled by CSS, so a signature drawn on
 * a high-density tablet is not a blurry rectangle when it is printed a year later. The second is
 * that pointer events cover finger, stylus and mouse with one set of handlers, which matters
 * because the same screen is used on a tablet at the desk and on a laptop when reception is
 * standing beside somebody.
 *
 * `onChange` is called with a data URI or with null when the pad is cleared, so the parent can keep
 * "has she signed yet" in one place and disable its own submit button.
 */
export function SignaturePad({
  onChange,
  disabled = false,
}: {
  readonly onChange: (dataUri: string | null) => void;
  readonly disabled?: boolean;
}) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, Math.round(rect.width * ratio));
    canvas.height = Math.max(1, Math.round(rect.height * ratio));

    const context = canvas.getContext('2d');
    if (context) {
      context.scale(ratio, ratio);
      context.lineWidth = 2;
      context.lineCap = 'round';
      context.lineJoin = 'round';
      context.strokeStyle = '#1c1b1a';
    }
  }, []);

  const positionOf = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const start = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) {
      return;
    }
    const context = canvasRef.current?.getContext('2d');
    if (!context) {
      return;
    }
    // Capture, so a finger that slides off the edge finishes its stroke rather than leaving the
    // pad convinced it is still being drawn on.
    event.currentTarget.setPointerCapture(event.pointerId);
    drawing.current = true;
    const { x, y } = positionOf(event);
    context.beginPath();
    context.moveTo(x, y);
  };

  const move = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) {
      return;
    }
    const context = canvasRef.current?.getContext('2d');
    if (!context) {
      return;
    }
    const { x, y } = positionOf(event);
    context.lineTo(x, y);
    context.stroke();
    if (!hasInk) {
      setHasInk(true);
    }
  };

  const end = () => {
    if (!drawing.current) {
      return;
    }
    drawing.current = false;
    const canvas = canvasRef.current;
    if (canvas) {
      onChange(canvas.toDataURL('image/png'));
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (canvas && context) {
      context.clearRect(0, 0, canvas.width, canvas.height);
    }
    setHasInk(false);
    onChange(null);
  };

  return (
    <div className="signature">
      <canvas
        ref={canvasRef}
        className="signature__pad"
        aria-label={t('clinical.signHere')}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
      />
      <div className="signature__actions">
        <span className="field__hint">{hasInk ? t('clinical.signed') : t('clinical.signHere')}</span>
        <button type="button" className="button button--ghost button--compact" onClick={clear}>
          {t('clinical.clearSignature')}
        </button>
      </div>
    </div>
  );
}
